import { useState } from 'react'
import {
    useUpdateMembershipMutation,
    useRefundMembershipPaymentMutation,
    useDisableAutoRenewalMutation,
    useCancelMembershipMutation,
} from '../../reduxQueries'
import { Member, MembershipPayment } from '../../schema'
import { removeEmptyStrings } from 'utils/requestPayload'
import { formatISOString } from 'utils/dates'

export const useMembershipMutations = (accountSlug: string, membershipUuid: string) => {
    const [updateMembership] = useUpdateMembershipMutation()
    const [refundPayment] = useRefundMembershipPaymentMutation()
    const [cancelMembership] = useCancelMembershipMutation()
    const [disableAutoRenewal] = useDisableAutoRenewalMutation()
    const [isUpdatingMember, setIsUpdatingMember] = useState(false)
    const [isRefundingPayment, setIsRefundingPayment] = useState(false)
    const [isUpdatingMembershipStatus, setIsUpdatingMembershipStatus] = useState(false)

    const handleResult = (result: any) => {
        if ('error' in result) {
            throw result.error
        }
        return result.data
    }

    const handleUpdateMember = async (values: Member, photo: string | null) => {
        setIsUpdatingMember(true)
        try {
            const data = {
                member: {
                    ...removeEmptyStrings(values),
                    dateOfBirth: values.dateOfBirth ? formatISOString(values.dateOfBirth, 'yyyy-MM-dd') : null,
                    photo: photo || null,
                },
            }
            const result = await updateMembership({ accountSlug, uuid: membershipUuid, overrides: data as any })
            return handleResult(result)
        } finally {
            setIsUpdatingMember(false)
        }
    }

    const handleRefundPayment = async (payment: MembershipPayment) => {
        setIsRefundingPayment(true)
        try {
            const result = await refundPayment({ accountSlug, uuid: membershipUuid, paymentUuid: payment.id })
            return handleResult(result)
        } finally {
            setIsRefundingPayment(false)
        }
    }

    const handleCancelMembership = async () => {
        setIsUpdatingMembershipStatus(true)
        try {
            const result = await cancelMembership({ accountSlug, uuid: membershipUuid })
            return handleResult(result)
        } finally {
            setIsUpdatingMembershipStatus(false)
        }
    }

    const handleDisableAutoRenewal = async () => {
        setIsUpdatingMembershipStatus(true)
        try {
            const result = await disableAutoRenewal({ accountSlug, uuid: membershipUuid })
            return handleResult(result)
        } finally {
            setIsUpdatingMembershipStatus(false)
        }
    }

    return {
        isUpdatingMember,
        isRefundingPayment,
        isUpdatingMembershipStatus,
        handleUpdateMember,
        handleRefundPayment,
        handleCancelMembership,
        handleDisableAutoRenewal,
    }
}
